import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import { atom, useAtom } from 'jotai'
import {
  CopyIcon,
  DownloadCloudIcon,
  ExternalLinkIcon,
  FileClockIcon,
  UploadCloudIcon,
  XIcon,
} from 'lucide-react'

import type { ValidationResponse } from './utils'
import { DISCORD_LINK } from '~/constants/content'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { useToast } from '~/design-system/atom/use-toast'
import { LoadingIconPlain } from '~/ui/components/base/loadingIcon'
import { copyText } from '~/utils/dom'
import { If } from '~/utils/reactComponent'
import {
  FileListHeader,
  Header,
  StepRow,
  Table,
  TableRow,
} from './component/table'
import { useOpenAPISpecs } from './hooks'
import { FinalizeStepModal, JobInfoModal } from './modal'
import { validateIntegrationYaml, validateSpecificationYaml } from './utils'

const OpenAPISpec = () => {
  const { openAPISpecs, isLoading } = useOpenAPISpecs({
    staleTime: 5000,
  })
  const [initialId, setInitialId] = useState<string | null>(null)

  return (
    <>
      <div className="m-auto mb-10 flex w-[640px] flex-col">
        <If condition={(openAPISpecs?.length ?? 0) < 1 && !isLoading}>
          <Link href={DISCORD_LINK}>
            <img
              src={'/assets/images/dashboard/openapi.svg'}
              alt="OpenAPI"
              className={clsx(
                'mb-6 mt-0 w-full',
                'user-drag-none user-select-none hover:filter-brightness(102%) cursor-pointer drop-shadow-[0_0_3px_rgba(0,0,0,0.1)]',
              )}
            />
          </Link>
        </If>
        <UpdateForm setInitialId={setInitialId} />
        <TableList initialId={initialId!} />
      </div>
    </>
  )
}

const UpdateForm = ({
  setInitialId,
}: {
  setInitialId: (id: string | null) => void
}) => {
  const [openapiSpec, setOpenapiSpec] = useState<string | null>(null)
  const [composioIntegrations, setComposioIntegrations] = useState<
    string | null
  >(null)
  const [openFinalize, setOpenFinalize] = useState(false)

  const { toast } = useToast()
  const onStartProcess = () => {
    if (!composioIntegrations) {
      toast({
        title: 'Please upload integration YAML',
        description: 'You need to upload integration YAML to start the process',
        variant: 'destructive',
      })
      return
    }
    setOpenFinalize(true)
  }

  const [, setFileData] = useAtom(fileAtoms)

  return (
    <>
      {openFinalize && (
        <FinalizeStepModal
          openAPISpec={openapiSpec || ''}
          integrationYML={composioIntegrations!}
          setOpen={() => {
            setOpenFinalize(false)
            setComposioIntegrations(null)
            setOpenapiSpec(null)
            setFileData({
              'file-upload-1': null,
              'file-upload-2': null,
            })
          }}
          onFinish={(data) => {
            setInitialId(data?.job_id as string)
            setOpenFinalize(false)
            setComposioIntegrations(null)
            setOpenapiSpec(null)
            setFileData({
              'file-upload-1': null,
              'file-upload-2': null,
            })
          }}
        />
      )}
      <Table>
        <Header />
        <div className=" mt-8 pl-4">
          <StepRow count={1}>
            <div className="flex w-full flex-col items-start">
              <h3 className=" font-avenir text-[14px] font-[500] text-[#242424]">
                Upload an OpenAPI spec
              </h3>
              <div className="mt-2 flex w-full items-center gap-1 text-[13px] font-[400] text-[#464646]">
                We’ll use the spec to create tools which your agent can use.
              </div>
              <div className="mt-3 flex items-center gap-3">
                <UploadButton
                  id="file-upload-1"
                  title="Upload YAML File"
                  onUpload={(data: string) => {
                    setOpenapiSpec(data)
                    return true
                  }}
                  validator={validateSpecificationYaml}
                />
                <p className=" flex items-center gap-1 text-[12px] text-black-500">
                  <a
                    className="flex items-center gap-1 text-black-400 underline "
                    href="https://github.com/ComposioHQ/sample-openapi-spec-for-download/blob/master/notion-openapi.yaml"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sample yaml <DownloadCloudIcon height={12} width={12} />
                  </a>
                </p>
              </div>
            </div>
          </StepRow>
          <StepRow count={2}>
            <div className="flex w-full flex-col items-start">
              <h3 className=" font-avenir text-[14px] font-[500] text-[#242424]">
                Upload Auth Config File
              </h3>
              <div className="mt-2 flex w-full items-center gap-1 text-[13px] font-[400] text-[#464646]">
                Specify auth method and other field for creation. We use this to
                validate the request.
              </div>
              <div className="mt-3 flex items-center gap-3">
                <UploadButton
                  id="file-upload-2"
                  title="Upload YAML File"
                  onUpload={(data: string) => {
                    setComposioIntegrations(data)
                    return true
                  }}
                  validator={validateIntegrationYaml}
                />
                <p className=" flex items-center gap-1 text-[12px] text-black-800">
                  <a
                    className="flex items-center gap-1 text-black-400 underline"
                    href="https://github.com/ComposioHQ/sample-openapi-spec-for-download/blob/master/notion_sample.yaml"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sample yaml <DownloadCloudIcon height={12} width={12} />
                  </a>
                  <a
                    className="ml-2 flex items-center gap-1 text-blue-700 underline"
                    href="https://dub.composio.dev/custom-tools"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Instructions <ExternalLinkIcon height={12} width={12} />
                  </a>
                </p>
              </div>
            </div>
          </StepRow>
          <StepRow count={3}>
            <div className="flex w-full flex-col items-start">
              <h3 className=" font-avenir text-[14px] font-[500] text-[#242424]">
                Start the process
              </h3>
              <div className="mt-2 flex w-full items-center gap-1 text-[13px] font-[400] text-[#464646]">
                This takes around 15-20 mins to complete.
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Button
                  variant="primary"
                  className="flex gap-1"
                  onClick={onStartProcess}
                >
                  Start import process
                </Button>
              </div>
            </div>
          </StepRow>
        </div>
      </Table>
    </>
  )
}

export const getStatusEmoji = (status: string) => {
  switch (status) {
    case 'initialized':
    case 'running':
      return '🔄'

    case 'exited':
      return '❌'

    case 'finished':
      return '✅'

    default:
      return '-'
  }
}

export const getStatus = (status: string) => {
  if (status === 'initialized') {
    return 'In processing. Please wait, we are building tools from your Spec.'
  }

  if (status === 'running') {
    return 'In processing. Please wait, we are building tools from your Spec.'
  }

  if (status === 'exited') {
    return 'Exited. Some error occurred, we will reach out to you soon.'
  }

  if (status === 'finished') {
    return 'Finished. You can now view and use your tools.'
  }
}

export const getStatusPlain = (status: string) => {
  if (status === 'initialized') {
    return 'In processing.'
  }

  if (status === 'running') {
    return 'In processing.'
  }

  if (status === 'exited') {
    return 'Exited.'
  }

  if (status === 'finished') {
    return 'Finished.'
  }
}

const TableList = ({ initialId }: { initialId: string }) => {
  const { openAPISpecs, isLoading } = useOpenAPISpecs({
    staleTime: 5000,
  })
  const [openJobId, setOpenJobId] = useState<string | null>(initialId)

  useEffect(() => {
    setOpenJobId(initialId)
  }, [initialId])

  const isEmptyOrLoading = isLoading || !openAPISpecs?.length

  const { toast } = useToast()

  if (isEmptyOrLoading) return null

  return (
    <div className="mt-6">
      <If condition={!!openJobId}>
        <JobInfoModal
          jobId={openJobId!}
          setOpen={() => {
            setOpenJobId(null)
          }}
        />
      </If>
      <Table>
        <FileListHeader />
        {isEmptyOrLoading && (
          <TableRow
            className="!h-[52px] py-2"
            left={
              <div className="flex h-full items-center justify-center gap-2">
                {isLoading && (
                  <>
                    <LoadingIconPlain height={20} width={20} /> Loading...
                  </>
                )}
                {!isLoading && !openAPISpecs?.length && (
                  <div>Please Upload Open API Spec to view tools.</div>
                )}
              </div>
            }
            right={<div></div>}
          />
        )}
        {openAPISpecs?.map((spec, index: number) => (
          <TableRow
            key={spec.id}
            left={
              <div className="flex flex-col items-start">
                <h3 className="mt-[2px] flex items-center gap-1 text-[13.5px] font-[500] text-[#242424]">
                  {spec.name}
                  <ToolTipBox
                    content={<div className="font-[400]">Copy ID</div>}
                  >
                    <div
                      onClick={() => {
                        copyText(spec.id)
                        toast({
                          title: 'Copied to clipboard',
                          description: 'Job ID copied to clipboard',
                          variant: 'success',
                        })
                      }}
                      className="ml-1 flex items-center gap-1 text-[12px] font-[500]"
                    >
                      <CopyIcon height={12} width={12} /> Job ID
                    </div>
                  </ToolTipBox>
                </h3>
                <p className="mt-1 flex items-center gap-1 text-[12px] text-black-600">
                  <span className="!text-[10px]">
                    {getStatusEmoji(spec.status)}
                  </span>
                  {getStatus(spec.status)}
                </p>
              </div>
            }
            className={clsx(
              'border-b-[1px] !border-grey-200',
              index === openAPISpecs.length - 1 && '!border-b-0',
            )}
            right={
              <div className="mt-1">
                <Button
                  variant="primary"
                  onClick={() => setOpenJobId(spec.id)}
                  size="36"
                >
                  <span className="mt-[2px]">Open</span>
                  <FileClockIcon height={12} width={12} />
                </Button>
              </div>
            }
          />
        ))}
      </Table>
    </div>
  )
}

const fileAtoms = atom({
  'file-upload-1': null,
  'file-upload-2': null,
})

const UploadButton = ({
  id,
  title,
  onUpload,
  validator,
  acceptedFormats = [
    'application/yml',
    'application/yaml',
    'text/yaml',
    'text/x-yaml',
  ],
}: {
  id: 'file-upload-1' | 'file-upload-2'
  title: string
  onUpload: (file: string) => boolean
  validator: (file: string) => ValidationResponse
  acceptedFormats?: string[]
}) => {
  const { toast } = useToast()
  const [fileData, setFileData] = useAtom(fileAtoms)
  const fileName = fileData[id] as string | null

  const setFile = (file: string | null) => {
    setFileData({ ...fileData, [id]: file })
  }

  const [uploadKey, setUploadKey] = useState<number>(0)
  // Added to force re-render of input after file selection
  const uploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]!
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = (event) => {
      if (event.target?.result) {
        const { errors } = validator(event.target.result as string)

        if (errors) {
          const message = errors.join(', ')

          toast({
            title: 'Upload failed',
            description: message,
            variant: 'destructive',
          })
        } else {
          if (onUpload(event.target.result as string)) {
            setFile(file.name)
          } else {
            toast({
              title: 'Upload failed',
              description: 'There was an issue processing your file.',
              variant: 'destructive',
            })
          }
        }
      }
    }
    setUploadKey((prevKey) => prevKey + 1) // Increment key to reset input
  }

  const handleDeleteFile = () => {
    setFile(null)
    toast({
      title: 'File removed',
      description: 'The selected file has been removed successfully.',
      variant: 'success',
    })
  }

  return (
    <>
      <label
        htmlFor={id}
        className={`custom-file-upload flex items-center gap-2 ${fileName ? 'custom-file-upload-filled' : ''}`}
      >
        {fileName ? (
          <div
            className="flex items-center gap-2"
            onClick={(e) => {
              e.preventDefault()
              handleDeleteFile()
            }}
          >
            {fileName &&
              (fileName.length > 20
                ? `${fileName.substring(0, 12)}...`
                : fileName)}
            <XIcon height={12} width={12} />
          </div>
        ) : (
          <>
            <UploadCloudIcon height={13} width={13} />
            {title || 'Upload'}
          </>
        )}
      </label>
      <input
        key={uploadKey}
        id={id}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={uploadFile}
        style={{
          display: 'none',
        }}
      />
    </>
  )
}

export { OpenAPISpec, UpdateForm, UploadButton }
